/* import __COLOCATED_TEMPLATE__ from './conversation-syncing-toggle.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';

interface Args {
  isConfigPendingSetup: boolean | undefined;
  platform: 'zendesk' | 'salesforce';
}

export default class ConversationSyncingToggle extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  get platform() {
    return this.args.platform;
  }

  get label() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.sync-history-label')
      : this.intl.t('standalone.zendesk.settings.connect.sync-history-label');
  }

  get descriptionText() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.sync-history-description')
      : this.intl.t('standalone.zendesk.settings.connect.sync-history-description');
  }

  get descriptionText2() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.sync-history-description-2')
      : this.intl.t('standalone.zendesk.settings.connect.sync-history-description-2');
  }

  get toggleText() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.sync-history-switch-label')
      : this.intl.t('standalone.zendesk.settings.connect.sync-history-switch-label');
  }

  get learnMoreLinkText() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.learn-more-about-syncing')
      : this.intl.t('standalone.zendesk.settings.connect.learn-more-about-syncing');
  }

  get importingEnabledNotificationMessage() {
    return this.platform === 'salesforce'
      ? this.intl.t('standalone.salesforce.setup.connect.conv-importing-enabled')
      : this.intl.t('standalone.zendesk.settings.connect.conv-importing-enabled');
  }

  @action
  async updateConversationSyncing() {
    // If we're disabling ask for confirmation
    if (this.finStandaloneService.isConversationSyncingEnabled) {
      let isConfirmed = await this.intercomConfirmService.confirm({
        body:
          this.platform === 'salesforce'
            ? this.intl.t('standalone.salesforce.setup.connect.disable-sync-body')
            : this.intl.t('standalone.zendesk.settings.connect.disable-sync-body'),
        confirmButtonText: this.intl.t('standalone.conv-syncing.disable-sync-continue'),
        title: this.intl.t('standalone.conv-syncing.disable-sync-title'),
      });

      if (!isConfirmed) {
        return;
      }

      this.finStandaloneService.isConversationSyncingEnabled = false;
    } else {
      this.finStandaloneService.isConversationSyncingEnabled = true;
    }

    await this.updateConversationSyncingState();
  }

  private async updateConversationSyncingState() {
    if (this.args.isConfigPendingSetup) {
      return;
    }
    try {
      await this.finStandaloneService.updateConversationSyncing();
      if (this.finStandaloneService.isConversationSyncingEnabled) {
        this.notificationsService.notifyConfirmation(this.importingEnabledNotificationMessage);
      }
    } catch (e) {
      this.finStandaloneService.isConversationSyncingEnabled =
        !this.finStandaloneService.isConversationSyncingEnabled;
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.toggle-conversation-sync-failure-notify'),
        },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ConversationSyncingToggle': typeof ConversationSyncingToggle;
    'standalone/common/conversation-syncing-toggle': typeof ConversationSyncingToggle;
  }
}
